import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';
import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  BRXM_ENDPOINT,
  SPA_BASE_URL,
  SSO_POST_LOGOUT_REDIRECT_PATH,
  SSO_ROOT,
} from '@/utils/constants';
import { getSSOLocationURL, isPathAbsolute } from '@/utils/helpers';
import { isProtectedPath } from '@/utils/portal';
import { addOidcEvents, removeOidcEvents } from './useOidcEvents';
import { useStorage } from '@/hooks/useStorage';
import { CALLBACK_REDIRECT_KEY } from '@/context/Oidc/constants';

export const hasCodeInUrl = (location: Location): boolean => {
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.replace('#', '?'));

  return Boolean(
    searchParams.get('code') ||
      searchParams.get('id_token') ||
      searchParams.get('session_state') ||
      hashParams.get('code') ||
      hashParams.get('id_token') ||
      hashParams.get('session_state'),
  );
};

export const useOidcUserManager = (configuration: UserManagerSettings) => {
  const history = useHistory();
  const { current: manager } = React.useRef(new UserManager(configuration));
  const [userData, setUserData] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isReady, setIsReady] = React.useState(false);
  const initialized = React.useRef(false);
  const [setCallbackRedirect, getCallbackRedirect] = useStorage<string>(
    sessionStorage,
    CALLBACK_REDIRECT_KEY,
  );

  /**
   * Load User Data
   */
  const loadUserData = React.useCallback(
    async (force = false) => {
      if (isLoading && !force) return null;

      setUserData(null);
      setIsLoading(true);

      const user = await manager.getUser();
      const hasExpired = !!user?.expired;

      if (!user || hasExpired) {
        const reason = user?.expired ? 'Token Expired' : 'Not Authenticated';

        // Redirect to the Sign In page if the user is not authenticated
        // and the current path is protected
        if (isProtectedPath()) {
          if (user) manager.removeUser();
          setCallbackRedirect(getSSOLocationURL());

          if (!hasExpired) {
            // Call the BRXM endpoint to obtain the _visitor cookie
            // before redirecting to the sign in page
            await fetch(`${BRXM_ENDPOINT}/ping`);
          }

          manager.signinRedirect();
          return Promise.reject(`${reason}, redirecting to sign in page...`);
        }

        // If the user is not authenticated and the current path is not protected
        // then just set the loading state to false and return null
        if (!force) {
          setTimeout(() => {
            setIsLoading(false);
            setIsReady(true);
          });

          return Promise.reject(reason);
        }
      }

      setUserData(user);

      setTimeout(() => {
        setIsLoading(false);
        setIsReady(true);
      });

      return user;
    },
    [isLoading, manager, setCallbackRedirect],
  );

  // Check if the user is returning back from OIDC.
  React.useEffect(() => {
    const init = async (): Promise<void> => {
      if (initialized.current) return;
      initialized.current = true;

      /**
       * Handle redirect after OIDC logout callback
       *
       * If the current URL contains the SSO_POST_LOGOUT_REDIRECT_PATH:
       * 1. For absolute callback URLs (e.g. https://...), redirect using window.location
       * 2. For relative URLs, use history.replace to redirect:
       *    - If callbackRedirect exists and doesn't start with SSO_ROOT, use that
       *    - Otherwise redirect to root ('/')
       * 3. Update loading and ready states
       */
      const isPostLogoutRedirect = window.location.pathname.includes(SSO_POST_LOGOUT_REDIRECT_PATH);
      if (isPostLogoutRedirect) {
        const redirectUrl = getCallbackRedirect();
        const shouldUseWindowLocation = redirectUrl && isPathAbsolute(redirectUrl);

        if (shouldUseWindowLocation) {
          window.location.href = redirectUrl;
          return;
        }

        const targetPath =
          redirectUrl && !redirectUrl.startsWith(SSO_ROOT) ? redirectUrl : SPA_BASE_URL;

        history.replace(targetPath);

        setTimeout(() => {
          setIsLoading(false);
          setIsReady(true);
        }, 0);

        return;
      }

      // Redirect to the callback page if the user is returning back from OIDC
      // through Login Callback
      if (hasCodeInUrl(window.location)) {
        const user = await manager.signinCallback();
        const redirectUrl = getCallbackRedirect();
        const shouldUseWindowLocation = redirectUrl && isPathAbsolute(redirectUrl);

        if (shouldUseWindowLocation) {
          window.location.href = redirectUrl;
          return;
        }

        const redirectPath = redirectUrl || SSO_ROOT;
        history.replace(redirectPath);

        setUserData(user as any);

        const updateLoadingState = () => {
          setIsLoading(false);
          setIsReady(true);
        };

        setTimeout(updateLoadingState, 0);
      } else {
        loadUserData(true).catch((error) => {
          console.error(error);
        });
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager]);

  /**
   * Reset User Data
   */
  const resetUserData = React.useCallback(() => setUserData(null), []);

  // Reset User Data on Token expiration
  React.useEffect(() => {
    addOidcEvents(manager, { onAccessTokenExpired: resetUserData });

    return () => {
      removeOidcEvents(manager, { onAccessTokenExpired: resetUserData });
    };
  }, [manager, resetUserData]);

  return {
    isReady,
    userData,
    isLoading,
    loadUserData,
    resetUserData,
    manager,
  };
};
